<script lang="ts">
  import {
    tasks, det, wbsNodes, statusRows, canEdit, allowWeekendWork,
    cycleStatusDay, projectStartDate, floorDay,
    leanView, selectedDay, boardFilter, clearBoardFilter, boardSortKey,
    pinTaskTo, moveTaskBy, unpinTask, reorderTaskAfter, useSwimLanes, twoShifts, projectPhases,
    bulkEditMode, bulkSelection, toggleBulkSelect,
  } from "../lib/stores";
  import { boardZoom, zoomBoardIn, zoomBoardOut, resetBoardZoom, BOARD_ZOOM_MIN, BOARD_ZOOM_MAX } from "../lib/boardZoom";
  import { onMount, tick } from "svelte";
  import { phaseColor } from "../lib/phases";
  import type { Phase } from "../lib/types";
  import { SHIFT_LABEL } from "../lib/shifts";
  import type { Shift } from "../lib/shifts";
  import { allTaskDays, cardMatches, compareCards, filterIsActive, matchingDays, requiredRowValue, resourcesForCards, statusConflicts, statusLabel, statusTone, tasksOnDayRequiringRow } from "../lib/board";
  import type { DayCard } from "../lib/board";
  import { formatProjectDay, isWeekendDay, projectDayLabel, projectStartDay, weekdayNameOfProjectDay, weekOfProjectDay } from "../lib/projectDate";
  import StickyCard from "./StickyCard.svelte";

  /** Which project days to draw, in order. The Plan view passes every day; the Week
   *  view and each printed page pass one week's worth. */
  export let days: number[] = [];
  /** Share the available width between the day columns instead of giving each a fixed
   *  width and scrolling. A week fits on a screen and on a sheet of paper; a whole
   *  changeover does not. */
  export let fit = false;
  /** Off for print, where drag targets, edit buttons and click affordances are noise. */
  export let interactive = true;

  $: editable = $canEdit && interactive;

  /** `cells[dayIndex][shiftIndex]` — a single-element shift axis when the project
   *  doesn't use shifts, so everything downstream works the same either way. */
  interface Lane { phase: Phase | null; cells: DayCard[][][] }
  interface WeekGroup { weekNum: number; span: number }

  let lanes: Lane[] = [];
  let weekGroups: WeekGroup[] = [];
  let dayCounts: number[] = [];
  let dayStarts: number[] = [];
  let dayEquipment: string[] = [];
  let dayPersonnel: string[] = [];
  let scheduleOk = false;
  $: shiftsPerDay = $twoShifts ? 2 : 1;
  $: shiftIndexes = Array.from({ length: shiftsPerDay }, (_, i) => i);
  /** The one day in the whole plan that's the entered start date itself — everything
   *  before it in week 1 is still shown, just flagged as not where work begins. */
  $: startDay = projectStartDay($projectStartDate);

  $: {
    const result = $det;
    scheduleOk = result.ok;
    if (result.ok) {
      const cards = allTaskDays($tasks, result, $allowWeekendWork, $twoShifts);
      allCards = cards;
      const slot: Record<number, number> = {};
      days.forEach((d, i) => (slot[d] = i));

      // A card whose task's phase isn't one of the project's current swimlanes (renamed
      // or deleted since) still gets its own trailing lane, keyed by that raw value —
      // a swimlane edit must never make existing work disappear from the board.
      const knownPhases = new Set($projectPhases.map((p) => p.name));
      const orphanPhases = [...new Set(cards.map((c) => c.task.phase))].filter((p) => !knownPhases.has(p));
      const lanePhases: (Phase | null)[] = $useSwimLanes ? [...$projectPhases.map((p) => p.name), ...orphanPhases] : [null];
      const phaseIdx: Record<string, number> = {};
      lanePhases.forEach((ph, i) => { if (ph != null) phaseIdx[ph] = i; });
      const matrix: DayCard[][][][] = lanePhases.map(() =>
        days.map(() => Array.from({ length: shiftsPerDay }, () => [] as DayCard[]))
      );
      const perDay: DayCard[][] = days.map(() => [] as DayCard[]);

      cards.forEach((c) => {
        const i = slot[c.day];
        const pi = $useSwimLanes ? phaseIdx[c.task.phase] : 0;
        if (i === undefined || pi === undefined) return;
        const si = $twoShifts && c.shift === 2 ? 1 : 0;
        matrix[pi][i][si].push(c);
        perDay[i].push(c);
      });
      matrix.forEach((row) => row.forEach((cell) => cell.forEach((shiftCards) => shiftCards.sort((a, b) => compareCards(a, b, $boardSortKey, result.ES, $wbsNodes)))));

      dayCounts = perDay.map((d) => d.length);
      dayStarts = perDay.map((d) => d.filter((c) => c.index === 1).length);
      dayEquipment = perDay.map((d) => resourcesForCards(d).equipment.join(", "));
      dayPersonnel = perDay.map((d) => resourcesForCards(d).personnel.join(", "));

      // Consecutive days from the same week share one heading spanning them.
      const groups: WeekGroup[] = [];
      days.forEach((d) => {
        const wk = weekOfProjectDay(d);
        const last = groups[groups.length - 1];
        if (last && last.weekNum === wk) last.span++;
        else groups.push({ weekNum: wk, span: 1 });
      });
      weekGroups = groups;
      lanes = lanePhases.map((ph, pi) => ({ phase: ph, cells: matrix[pi] }));
    } else {
      lanes = []; weekGroups = []; dayCounts = []; dayStarts = [];
      dayEquipment = []; dayPersonnel = [];
    }
  }

  // ---- Filtering and scroll navigation ----
  // The full plan is roughly ten screens wide with nothing to orient by, so it needs a
  // way to find a task and a way to get back to today.

  let allCards: DayCard[] = [];
  $: filterOn = filterIsActive($boardFilter);
  $: hits = filterOn ? matchingDays(allCards, $boardFilter, $wbsNodes) : [];
  $: hitCount = filterOn ? allCards.filter((c) => cardMatches(c, $boardFilter, $wbsNodes)).length : 0;

  /** Weekends can't hold work when the schedule refuses to place it there, so they're
   *  narrowed to a gutter — on a 55-day plan that's two of every seven columns. */
  $: gutterDays = !$allowWeekendWork;
  function isGutter(day: number): boolean {
    return gutterDays && isWeekendDay(day);
  }

  let wrapEl: HTMLElement | undefined;
  let headCells: (HTMLElement | undefined)[] = [];
  // Matches th.lean-lane-label's base (unzoomed) width in app.css — scaled by the same
  // --lean-zoom factor so the scroll math still clears the pinned column at any zoom.
  const LANE_LABEL_WIDTH = 150;

  /** Brings a day's column clear of the pinned lane-label column, which would otherwise
   *  sit on top of whatever we just scrolled to. */
  function scrollToDay(day: number) {
    const i = days.indexOf(day);
    const th = headCells[i];
    if (!wrapEl || !th) return;
    const delta = th.getBoundingClientRect().left - wrapEl.getBoundingClientRect().left;
    wrapEl.scrollTo({ left: wrapEl.scrollLeft + delta - (LANE_LABEL_WIDTH + 24) * $boardZoom, behavior: "smooth" });
  }

  let hitIdx = 0;
  $: if (filterOn && hitIdx >= hits.length) hitIdx = 0;
  function stepHit(delta: number) {
    if (!hits.length) return;
    hitIdx = (hitIdx + delta + hits.length) % hits.length;
    scrollToDay(hits[hitIdx]);
  }

  function jumpToToday() {
    if ($floorDay != null) scrollToDay($floorDay);
  }

  // Opening on week 1 of a plan that started a month ago is the wrong default, but only
  // on first render — re-centring on every recalculation would fight the user's scrolling.
  onMount(async () => {
    if (!interactive || fit || $floorDay == null) return;
    await tick();
    scrollToDay($floorDay);
  });

  /** Toasts read mid-sentence, where a bare project-day number means nothing to
   *  anyone standing on site. */
  function dayLabel(day: number): string {
    return projectDayLabel($projectStartDate, day, "day");
  }

  function openDayInTodayView(day: number) {
    if (!interactive) return;
    selectedDay.set(day);
    leanView.set("today");
  }

  // Every rescheduling gesture the board offers is the same effect on the plan, so
  // each handler is just the gesture: what it means lives in the store.
  function onCardMove(e: CustomEvent<{ id: string; dayDelta: number; phaseDelta: number; shiftDelta: number }>) {
    moveTaskBy(e.detail.id, e.detail.dayDelta, e.detail.phaseDelta, e.detail.shiftDelta);
  }

  function onCardUnpin(e: CustomEvent<{ id: string }>) {
    unpinTask(e.detail.id);
  }

  // With swimlanes off there's only one lane, so a drop there can't tell us a phase —
  // keep whatever phase the task already had and only move the day.
  function onDrop(e: DragEvent, day: number, targetPhase: Phase | null, shift?: Shift) {
    e.preventDefault();
    (e.currentTarget as HTMLElement).classList.remove("drag-over");
    const id = e.dataTransfer?.getData("text/plain");
    if (!id) return;
    const phase = targetPhase ?? $tasks.find((t) => t.id === id)?.phase;
    if (phase) pinTaskTo(id, day, phase, shift);
  }

  function onCardReorder(e: CustomEvent<{ draggedId: string; targetId: string }>, day: number, targetPhase: Phase | null, shift?: Shift) {
    const phase = targetPhase ?? $tasks.find((t) => t.id === e.detail.draggedId)?.phase;
    if (phase) reorderTaskAfter(e.detail.draggedId, e.detail.targetId, day, phase, shift);
  }

  /** The shift a drop/reorder in this sub-column should set — undefined when the
   *  project doesn't split days into shifts, so the task's existing shift is kept. */
  function shiftOf(si: number): Shift | undefined {
    return $twoShifts ? (si === 1 ? 2 : 1) : undefined;
  }

  function onDragOver(e: DragEvent) {
    if (!interactive) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    (e.currentTarget as HTMLElement).classList.add("drag-over");
  }
  function onDragLeave(e: DragEvent) {
    (e.currentTarget as HTMLElement).classList.remove("drag-over");
  }
</script>

{#if interactive && scheduleOk}
  <div class="board-nav">
    <div class="board-search">
      <input
        type="search"
        placeholder="Find WBS, scope, activity, party or kit…"
        aria-label="Filter the board"
        bind:value={$boardFilter.text}
      />
      {#if filterOn}
        <span class="board-hits" role="status">
          {hitCount} {hitCount === 1 ? "match" : "matches"}
          {#if hits.length}· {hits.length} {hits.length === 1 ? "day" : "days"}{/if}
        </span>
      {/if}
    </div>

    <label class="board-chip"><input type="checkbox" bind:checked={$boardFilter.critical} /> Critical path</label>
    <label class="board-chip"><input type="checkbox" bind:checked={$boardFilter.blocked} /> Blocked</label>
    <label class="board-chip"><input type="checkbox" bind:checked={$boardFilter.unfinished} /> Not done</label>

    {#if filterOn}
      <button class="board-btn" type="button" on:click={clearBoardFilter}>Clear</button>
    {/if}

    <div class="board-nav-right">
      {#if !fit}
        {#if filterOn && hits.length}
          <button class="board-btn" type="button" title="Previous day with a match" on:click={() => stepHit(-1)}>‹</button>
          <span class="board-hit-pos">{hitIdx + 1} / {hits.length}</span>
          <button class="board-btn" type="button" title="Next day with a match" on:click={() => stepHit(1)}>›</button>
        {/if}
        {#if $floorDay != null}
          <button class="board-btn" type="button" on:click={jumpToToday}>Jump to today</button>
        {/if}
      {/if}
      <div class="board-zoom" role="group" aria-label="Board zoom">
        <button class="board-btn" type="button" title="Zoom out" aria-label="Zoom out" disabled={$boardZoom <= BOARD_ZOOM_MIN} on:click={zoomBoardOut}>－</button>
        <span class="board-zoom-level">{Math.round($boardZoom * 100)}%</span>
        <button class="board-btn" type="button" title="Zoom in" aria-label="Zoom in" disabled={$boardZoom >= BOARD_ZOOM_MAX} on:click={zoomBoardIn}>＋</button>
        {#if $boardZoom !== 1}
          <button class="board-btn" type="button" title="Reset zoom to 100%" on:click={resetBoardZoom}>Reset</button>
        {/if}
      </div>
    </div>
  </div>
{/if}

{#if interactive && !fit && scheduleOk}
  <!-- Every sticky is a tab stop, and the full plan holds dozens of them; without this
       reaching anything past the board means tabbing through all of them. -->
  <a class="skip-board" href="#after-lean-board">Skip the board</a>
{/if}

<div class="lean-board-wrap" class:fit bind:this={wrapEl} style="--lean-zoom: {$boardZoom}">
  <table class="lean-table" class:fit>
    {#if scheduleOk}
      <colgroup>
        <col class="lane-col" />
        {#each days as d (d)}
          {#each shiftIndexes as si (si)}
            <col class="day-col" class:gutter={isGutter(d)} />
          {/each}
        {/each}
      </colgroup>
    {/if}
    <thead>
      {#if scheduleOk}
        {@const cornerRowspan = (fit ? 1 : 2) + ($twoShifts ? 1 : 0)}
        {#if !fit}
          <!-- Only the full-plan board needs week headings inside the table; the
               windowed views already name the week in their own heading. -->
          <tr>
            <th class="lean-corner-cell" rowspan={cornerRowspan}></th>
            {#each weekGroups as wk, i (i)}
              <th class="lean-week-head-cell" colspan={wk.span * shiftsPerDay}>Week {wk.weekNum}</th>
            {/each}
          </tr>
        {/if}
        <tr>
          {#if fit}<th class="lean-corner-cell" rowspan={cornerRowspan}></th>{/if}
          {#each days as day, i (day)}
            {@const full = formatProjectDay($projectStartDate, day) ?? `Day ${day} · ${weekdayNameOfProjectDay(day)}`}
            {@const isStart = day === startDay}
            <th class="lean-day-head-cell" class:weekend={isWeekendDay(day)} class:gutter={isGutter(day)}
              class:is-today={day === $floorDay} class:is-start={isStart} colspan={shiftsPerDay} bind:this={headCells[i]}>
              {#if interactive}
                <button class="lean-day-title" type="button"
                  title={isGutter(day) ? `${full} — non-working day` : isStart ? `${full} — project start` : "Open this day in the standup view"}
                  on:click={() => openDayInTodayView(day)}>
                  {isGutter(day) ? weekdayNameOfProjectDay(day).slice(0, 2) : full}
                </button>
              {:else}
                <div class="lean-day-title static">
                  {isGutter(day) ? weekdayNameOfProjectDay(day).slice(0, 2) : full}
                </div>
              {/if}
              {#if !isGutter(day)}
                <div class="lean-day-count">
                  {#if isStart}<span class="day-start-tag">Start</span>{/if}
                  {#if dayStarts[i]}<span class="starts">{dayStarts[i]} starting</span>{/if}
                  {#if dayCounts[i] - dayStarts[i] > 0}<span class="running">{dayCounts[i] - dayStarts[i]} running</span>{/if}
                  {#if !dayCounts[i]}<span class="running">—</span>{/if}
                </div>
              {/if}
            </th>
          {/each}
        </tr>
        {#if $twoShifts}
          <tr class="lean-shift-head-row">
            {#each days as day (day)}
              <th class="lean-shift-head-cell" class:gutter={isGutter(day)}>{SHIFT_LABEL[1]}</th>
              <th class="lean-shift-head-cell shift-2" class:gutter={isGutter(day)}>{SHIFT_LABEL[2]}</th>
            {/each}
          </tr>
        {/if}
      {/if}
    </thead>
    <tbody>
      {#if !scheduleOk}
        <tr><td class="lean-empty">Fix the schedule error above to see the board.</td></tr>
      {:else}
        {#each $statusRows as row (row.id)}
          <tr class="lean-status-row">
            <th class="lean-lane-label">
              <div class="lean-lane-inner">
                <span class="lean-lane-name">{row.label}</span>
              </div>
            </th>
            {#each days as day (day)}
              {@const explicit = row.values[day] ?? null}
              {@const dependents = tasksOnDayRequiringRow(allCards, day, row.id)}
              {@const required = requiredRowValue(dependents, row.id)}
              {@const suggested = explicit == null && required != null}
              {@const v = explicit ?? required}
              {@const tone = statusTone(row, v)}
              {@const dependentsNote = dependents.length ? ` Depended on by: ${dependents.map((t) => t.name).join(", ")}.` : ""}
              {@const hasConflict = explicit != null && required != null && explicit !== required}
              <td class="lean-status-cell" class:good={tone === "good"} class:attention={tone === "attention"}
                class:unset={tone === "unset"} class:suggested={suggested} class:weekend={isWeekendDay(day)} class:gutter={isGutter(day)}
                class:readonly={!editable} class:has-conflict={hasConflict} colspan={shiftsPerDay}>
                {#if editable}
                  <button
                    class="status-toggle"
                    type="button"
                    aria-label="{row.label}, {dayLabel(day)}: {statusLabel(row, v)}{suggested ? ' (suggested, not confirmed)' : ''}.{dependentsNote} Activate to change."
                    title="Click to cycle: not set → {row.onLabel} → {row.offLabel}.{dependentsNote}"
                    on:click={() => cycleStatusDay(row.id, day, required)}
                  >{statusLabel(row, v)}</button>
                {:else}
                  <span title={dependentsNote || undefined}>{statusLabel(row, v)}</span>
                {/if}
              </td>
            {/each}
          </tr>
        {/each}
        <tr class="lean-status-row derived">
          <th class="lean-lane-label">
            <div class="lean-lane-inner"><span class="lean-lane-name">🔧 Equipment in use</span></div>
          </th>
          {#each days as day, i (day)}
            <td class="lean-status-cell derived" class:weekend={isWeekendDay(day)} colspan={shiftsPerDay}>{dayEquipment[i] || "—"}</td>
          {/each}
        </tr>
        <tr class="lean-status-row derived">
          <th class="lean-lane-label">
            <div class="lean-lane-inner"><span class="lean-lane-name">👷 Personnel needed</span></div>
          </th>
          {#each days as day, i (day)}
            <td class="lean-status-cell derived" class:weekend={isWeekendDay(day)} colspan={shiftsPerDay}>{dayPersonnel[i] || "—"}</td>
          {/each}
        </tr>
        {#each lanes as lane, li (lane.phase ?? "__all__")}
          <tr class:first-lane={li === 0}>
            <th class="lean-lane-label">
              <div class="lean-lane-inner">
                {#if lane.phase}
                  <span class="lean-lane-dot" style="background:{phaseColor($projectPhases, lane.phase)}"></span>
                  <span class="lean-lane-name">{lane.phase}</span>
                {:else}
                  <span class="lean-lane-name">All phases</span>
                {/if}
              </div>
            </th>
            {#each lane.cells as dayShifts, i (days[i])}
              {#each dayShifts as entries, si (si)}
                <td class="lean-cell-td" class:weekend={isWeekendDay(days[i])} class:gutter={isGutter(days[i])}
                  class:shift-2={$twoShifts && si === 1}
                  on:dragover={onDragOver}
                  on:dragleave={onDragLeave}
                  on:drop={(e) => interactive && onDrop(e, days[i], lane.phase, shiftOf(si))}
                >
                  <div class="lean-cell">
                    {#each entries as entry (entry.task.id)}
                      <StickyCard t={entry.task} critical={entry.critical} span={entry.span} spanIdx={entry.index}
                        done={entry.done} {interactive}
                        dimmed={filterOn && !cardMatches(entry, $boardFilter, $wbsNodes)}
                        statusConflicts={statusConflicts(entry, $statusRows)}
                        selectable={$bulkEditMode} selected={$bulkSelection.has(entry.task.id)}
                        on:reorder={(e) => onCardReorder(e, days[i], lane.phase, shiftOf(si))}
                        on:move={onCardMove}
                        on:unpin={onCardUnpin}
                        on:toggleSelect={(e) => toggleBulkSelect(e.detail.id)} />
                    {/each}
                  </div>
                </td>
              {/each}
            {/each}
          </tr>
        {/each}
      {/if}
    </tbody>
  </table>
</div>
<span id="after-lean-board" tabindex="-1"></span>
